import {
  ForwardToInbox,
  ImageSearchOutlined,
  MobileScreenShare,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  Card as MUICard,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { ImageLoader, LockButton, useMenu } from '@prismamedia/one-components';
import { RawUser } from '@prismamedia/one-user';
import clsx from 'clsx';
import { format, format as formatDate } from 'date-fns';
import fr from 'date-fns/locale/fr';
import { FC, ReactNode, useMemo, useState } from 'react';
import { GetArticlesQuery_query_articles_Article_newsletterFlashSenderHistories_NewsletterFlashSenderHistory } from '../../__generated__/queries-web';
import {
  CREATED_AT,
  EMPTY_TITLE,
  END_EDITION_TEXT,
  PUSH_LABEL,
  UPDATED_AT,
} from './constants';
import { useStyles } from './styles';

export enum CardSize {
  LARGE = 'LARGE',
  MEDIUM = 'MEDIUM',
  SMALL = 'SMALL',
}

interface CardItemProps {
  ActionsIcons?: ReactNode;
  ActionsMenu?: ReactNode;
  Icons?: ReactNode;
  children?: ReactNode;
  createdAt?: string | null;
  format?: string;
  id: string;
  media: string | null;
  onCardClick?: (id: string) => () => void;
  lockedBy?: Partial<Pick<RawUser, 'name' | 'avatarUrl'>> | null;
  isLocked?: boolean;
  updateLocker?: (
    id: string,
    lockerId: string | null,
    noCache?: boolean,
  ) => void;
  size?: CardSize;
  status?: string;
  title: string | null;
  topTitle?: string;
  updatedAt?: string | null;
  brandKey?: string;
  eventTitle?: string;
  categoryTitle?: string;
  dataTestId?: string;
  isPaidArticle?: boolean;
  statusTitle?: string;
  hasPush?: boolean;
  pushCount?: number;
  titleOverflow?: boolean;
  newsletterFlashSenderHistories?: GetArticlesQuery_query_articles_Article_newsletterFlashSenderHistories_NewsletterFlashSenderHistory[];
}

export const CardItem: FC<CardItemProps> = ({
  ActionsIcons,
  ActionsMenu,
  Icons,
  children,
  createdAt,
  id,
  media,
  onCardClick,
  lockedBy,
  isLocked,
  updateLocker,
  size = CardSize.LARGE,
  title,
  topTitle,
  updatedAt,
  brandKey,
  eventTitle,
  categoryTitle,
  dataTestId,
  isPaidArticle,
  statusTitle,
  hasPush,
  pushCount,
  titleOverflow,
  newsletterFlashSenderHistories,
}) => {
  const theme = useTheme();
  const { openMenu, closeMenu } = useMenu();
  const [isCardOver, setIsCardOver] = useState(false);
  const classes = useStyles({ isCardOver, size });

  const topTitleDisplayed = useMemo(() => {
    if (topTitle) {
      return topTitle;
    }

    let createdAtText = createdAt
      ? `${CREATED_AT} ${formatDate(new Date(createdAt), 'dd/MM/yyyy à HH:mm', {
          locale: fr,
        })}`
      : null;

    const updatedAtText = updatedAt
      ? `${UPDATED_AT} ${formatDate(new Date(updatedAt), 'dd/MM/yyyy à HH:mm', {
          locale: fr,
        })}`
      : null;

    const isTopTitleEmpty = !createdAtText?.length && !updatedAtText?.length;

    return [
      createdAtText ? (createdAtText += updatedAtText ? ' - ' : '') : '',
      updatedAtText ?? '',
      !isTopTitleEmpty ? '.' : '',
    ].join('');
  }, [createdAt, updatedAt, topTitle]);

  return (
    <MUICard
      className={classes.wrapper}
      onClick={onCardClick?.(id)}
      onMouseLeave={() => {
        setIsCardOver(false);
      }}
      onMouseOver={() => {
        setIsCardOver(true);
      }}
      variant="outlined"
      {...(dataTestId && { 'data-testid': dataTestId })}
    >
      <div className={classes.innerWrapper}>
        {/* Card media thumbnail */}
        <div className={classes.wrapperLeftSide}>
          <Typography className={classes.brandkey}>{brandKey}</Typography>
          <ImageLoader
            className={classes.media}
            image={<ImageSearchOutlined className={classes.imageFallback} />}
            loaderSize={35}
            src={media}
            timeout={3000}
          />
        </div>

        {/* Card Title & description */}
        <div className={classes.wrapperCenter}>
          <div className={classes.titleWrapper}>
            {isPaidArticle && <div className={classes.paidArticle}>★</div>}
            <Typography
              sx={{
                color: theme.palette.grey[500],
                marginBottom: theme.spacing(0.5),
                fontSize:
                  size !== CardSize.LARGE
                    ? theme.typography.caption.fontSize
                    : 'inherit',
                marginRight: theme.spacing(2),
              }}
            >
              {topTitleDisplayed}
              {hasPush && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    verticalAlign: 'middle',
                    ml: 0.5,
                  }}
                >
                  {' | '}
                  <Tooltip title={`${PUSH_LABEL} ${pushCount || 0}`}>
                    <MobileScreenShare
                      fontSize="small"
                      sx={{
                        color: grey[500],
                        verticalAlign: 'middle',
                      }}
                    />
                  </Tooltip>
                </Box>
              )}
              {!!newsletterFlashSenderHistories?.length && (
                <>
                  {' | '}
                  <Tooltip
                    title={
                      <Box sx={{ whiteSpace: 'pre-line' }}>
                        {newsletterFlashSenderHistories
                          .map(
                            (history) =>
                              `NL Flash envoyé le ${format(
                                new Date(history.createdAt),
                                'Pp',
                              )}`,
                          )
                          .join('\n')}
                      </Box>
                    }
                  >
                    <ForwardToInbox
                      fontSize="small"
                      sx={{
                        ml: 0.5,
                        color: grey[500],
                        verticalAlign: 'middle',
                      }}
                    />
                  </Tooltip>
                </>
              )}
            </Typography>
          </div>

          <Typography
            variant="h5"
            component="h2"
            className={clsx(!titleOverflow && classes.mainTitle)}
          >
            {title || <em className={classes.empty}>{EMPTY_TITLE}</em>}
          </Typography>

          {statusTitle && (
            <Chip
              className={classes.status}
              label={statusTitle}
              variant="outlined"
              size="small"
            />
          )}

          {/* Action Icons/Menu (top/right) */}
          <div className={classes.wrapperActionsIcons}>
            {ActionsIcons} {children || ActionsMenu}
          </div>

          {/* Event and Category Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {eventTitle && (
              <Typography className={classes.eventTitle} variant="caption">
                {eventTitle}
              </Typography>
            )}
            {eventTitle && categoryTitle && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                |
              </Typography>
            )}
            {categoryTitle && (
              <Typography className={classes.categoryTitle} variant="caption">
                {categoryTitle}
              </Typography>
            )}
          </Box>
        </div>

        {/* Card Icons */}
        <div className={classes.wrapperRightSide}>
          {Icons}

          {lockedBy && (
            <LockButton
              className={classes.lockButton}
              disableLockForV0
              lockedBy={lockedBy}
              onClick={(e) => {
                e.preventDefault();
                if (isLocked) {
                  openMenu(
                    e.currentTarget,
                    <MenuItem
                      onClick={() => {
                        updateLocker?.(id, null);
                        closeMenu();
                      }}
                    >
                      {END_EDITION_TEXT}
                    </MenuItem>,
                  );
                }
              }}
              size="small"
            />
          )}
        </div>
      </div>
    </MUICard>
  );
};
